import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { WikipediaArticle, ViewMode, InterestProfile, ViewSignal, ReadingStats } from './types.ts';
import { fetchArticleBatch, fetchArticleByTitle, updateInterestProfile, downvoteInterestProfile, processViewSignals } from './services/wikipedia.ts';
import { ArticleCard } from './components/ArticleCard.tsx';
import { Onboarding } from './components/Onboarding.tsx';
import { ProfileSettings } from './components/ProfileSettings.tsx';
import { Search, X, RefreshCw, Home, Heart, ArrowLeft, User, Compass } from 'lucide-react';

// Skeleton card shown while first articles load
const SkeletonCard: React.FC = () => (
  <div className="relative w-full h-[100dvh] snap-start flex flex-col bg-black overflow-hidden border-b border-white/5">
    <div className="relative w-full h-[50vh] bg-[#050505] flex items-center justify-center overflow-hidden">
      <div className="w-full h-full bg-white/[0.03] animate-pulse" />
    </div>
    <div className="flex-1 bg-[#080808] p-6 pt-5 flex flex-col border-t border-white/5">
      <div className="max-w-xl mx-auto w-full flex flex-col gap-4">
        <div className="h-3 w-24 bg-white/[0.06] rounded-full animate-pulse" />
        <div className="h-7 w-3/4 bg-white/[0.08] rounded-lg animate-pulse" />
        <div className="h-5 w-1/2 bg-white/[0.06] rounded-lg animate-pulse" />
        <div className="space-y-3 mt-2">
          <div className="h-4 w-full bg-white/[0.04] rounded animate-pulse" />
          <div className="h-4 w-full bg-white/[0.04] rounded animate-pulse" />
          <div className="h-4 w-5/6 bg-white/[0.04] rounded animate-pulse" />
          <div className="h-4 w-4/6 bg-white/[0.04] rounded animate-pulse" />
        </div>
      </div>
    </div>
  </div>
);

const INITIAL_BATCH = 10;
const SUBSEQUENT_BATCH = 15;
const PREFETCH_THRESHOLD = 5;
const SEEN_IDS_KEY = 'scrolling_seen_ids';
const MAX_SEEN_IDS = 500;
const STATS_KEY = 'scrolling_stats';
const CACHED_ARTICLES_KEY = 'scrolling_cached_articles';
const MAX_CACHED = 8;
const DISMISSED_IDS_KEY = 'scrolling_dismissed';

const getToday = () => new Date().toISOString().split('T')[0];

const loadStats = (): ReadingStats => {
  try {
    const saved = localStorage.getItem(STATS_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {}
  return { articlesViewed: 0, readingTimeMs: 0, articlesLiked: 0, currentStreak: 0, lastActiveDate: '' };
};

const updateStreak = (stats: ReadingStats): ReadingStats => {
  const today = getToday();
  if (stats.lastActiveDate === today) return stats;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];

  return {
    ...stats,
    currentStreak: stats.lastActiveDate === yesterdayStr ? stats.currentStreak + 1 : 1,
    lastActiveDate: today
  };
};

const loadCachedArticles = (): WikipediaArticle[] => {
  try {
    const saved = localStorage.getItem(CACHED_ARTICLES_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {}
  return [];
};

const App: React.FC = () => {
  const [articles, setArticles] = useState<WikipediaArticle[]>(loadCachedArticles);
  const [exploreArticles, setExploreArticles] = useState<WikipediaArticle[]>([]);
  const [searchResults, setSearchResults] = useState<WikipediaArticle[]>([]);
  const [likedArticles, setLikedArticles] = useState<WikipediaArticle[]>(() => {
    try {
      const saved = localStorage.getItem('scrolling_liked');
      return saved ? JSON.parse(saved) : [];
    } catch (e) { return []; }
  });

  const [interestProfile, setInterestProfile] = useState<InterestProfile>(() => {
    try {
      const saved = localStorage.getItem('scrolling_profile');
      return saved ? JSON.parse(saved) : {};
    } catch (e) { return {}; }
  });

  const [readingStats, setReadingStats] = useState<ReadingStats>(() => updateStreak(loadStats()));

  const [viewMode, setViewMode] = useState<ViewMode>('feed');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(() => loadCachedArticles().length === 0);
  const [exploreLoading, setExploreLoading] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFullArticleOpen, setIsFullArticleOpen] = useState(false);

  // Onboarding & Profile
  const [userInterests, setUserInterests] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('scrolling_interests');
      return saved ? JSON.parse(saved) : null;
    } catch (e) { return null; }
  });
  const [showOnboarding, setShowOnboarding] = useState(userInterests === null);
  const [showProfileSettings, setShowProfileSettings] = useState(false);

  const searchOffset = useRef(0);
  const seenIds = useRef<Set<number>>((() => {
    try {
      const seen = localStorage.getItem(SEEN_IDS_KEY);
      const dismissed = localStorage.getItem(DISMISSED_IDS_KEY);
      const seenArr: number[] = seen ? JSON.parse(seen) : [];
      const dismissedArr: number[] = dismissed ? JSON.parse(dismissed) : [];
      return new Set([...seenArr.slice(-MAX_SEEN_IDS), ...dismissedArr]);
    } catch (e) { return new Set<number>(); }
  })());
  const exploreSeenIds = useRef<Set<number>>(new Set());
  const searchSeenIds = useRef<Set<number>>(new Set());
  const isInitialLoadRequested = useRef(false);
  const mainRef = useRef<HTMLDivElement>(null);
  const scrollPositions = useRef<Record<string, number>>({ feed: 0, search: 0, liked: 0, explore: 0 });

  // View-time tracking refs (no re-renders)
  const currentVisibleIndex = useRef<number>(0);
  const viewStartTime = useRef<number>(Date.now());
  const accumulatedModalTime = useRef<number>(0);
  const pendingSignals = useRef<ViewSignal[]>([]);
  const scrollDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hiddenAtTime = useRef<number>(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preloadedUrls = useRef<Set<string>>(new Set());

  // Persist stats
  useEffect(() => {
    localStorage.setItem(STATS_KEY, JSON.stringify(readingStats));
  }, [readingStats]);

  useEffect(() => {
    localStorage.setItem('scrolling_profile', JSON.stringify(interestProfile));
  }, [interestProfile]);

  useEffect(() => {
    localStorage.setItem('scrolling_liked', JSON.stringify(likedArticles));
  }, [likedArticles]);

  const likedIds = useMemo(() => new Set(likedArticles.map(a => a.pageid)), [likedArticles]);

  // Record a view signal for the article at the given index
  const recordCurrentView = useCallback(() => {
    const idx = currentVisibleIndex.current;
    const currentList = viewMode === 'explore' ? exploreArticles : articles;
    if (idx < 0 || idx >= currentList.length) return;
    const article = currentList[idx];
    if (!article) return;

    const now = Date.now();
    const duration = now - viewStartTime.current;

    pendingSignals.current.push({
      article,
      duration,
      modalDuration: accumulatedModalTime.current,
      isLiked: likedIds.has(article.pageid),
    });

    accumulatedModalTime.current = 0;

    // Update reading stats
    setReadingStats(prev => updateStreak({
      ...prev,
      articlesViewed: prev.articlesViewed + 1,
      readingTimeMs: prev.readingTimeMs + duration,
    }));
  }, [articles, exploreArticles, viewMode, likedIds]);

  // Flush pending signals to update the interest profile
  const flushSignals = useCallback(() => {
    if (pendingSignals.current.length === 0) return;
    const signals = pendingSignals.current.splice(0);
    setInterestProfile(current => processViewSignals(current, signals));
  }, []);

  // Schedule periodic flush every 10 seconds
  useEffect(() => {
    flushTimerRef.current = setInterval(() => {
      flushSignals();
    }, 10000);
    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    };
  }, [flushSignals]);

  // Tab visibility: pause/resume view timer
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        hiddenAtTime.current = Date.now();
      } else {
        if (hiddenAtTime.current > 0) {
          const hiddenDuration = Date.now() - hiddenAtTime.current;
          viewStartTime.current += hiddenDuration;
          hiddenAtTime.current = 0;
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Before unload: record current article + flush synchronously to localStorage
  useEffect(() => {
    const handleUnload = () => {
      recordCurrentView();
      if (pendingSignals.current.length > 0) {
        const signals = pendingSignals.current.splice(0);
        try {
          const saved = localStorage.getItem('scrolling_profile');
          const current: InterestProfile = saved ? JSON.parse(saved) : {};
          const updated = processViewSignals(current, signals);
          localStorage.setItem('scrolling_profile', JSON.stringify(updated));
        } catch (e) { /* ignore */ }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [recordCurrentView]);

  // Callback for modal time from ArticleCard (stable ref — doesn't break memo)
  const handleModalTime = useCallback((durationMs: number) => {
    accumulatedModalTime.current += durationMs;
  }, []);

  const loadMore = useCallback(async (reset = false, overrideInterests?: string[]) => {
    if (!reset && loading) return;
    setLoading(true);
    if (reset) {
      setError(null);
      searchOffset.current = 0;
    }

    try {
      const excludeIds = new Set<number>(seenIds.current);
      likedIds.forEach(id => excludeIds.add(id));

      const result = await fetchArticleBatch(
        reset ? INITIAL_BATCH : SUBSEQUENT_BATCH,
        excludeIds,
        'feed',
        {
          profile: interestProfile,
          userInterests: overrideInterests || userInterests || []
        }
      );

      const newOnes = result.articles || [];

      if (reset) {
        setArticles(newOnes);
        setInitialLoading(false);
        if (newOnes.length === 0) setError("No articles found.");
        // Cache articles for instant load next visit
        try {
          localStorage.setItem(CACHED_ARTICLES_KEY, JSON.stringify(newOnes.slice(0, MAX_CACHED)));
        } catch (e) {}
      } else {
        setArticles(prev => [...prev, ...newOnes]);
      }

      newOnes.forEach(a => seenIds.current.add(a.pageid));
      try {
        const ids = Array.from(seenIds.current).slice(-MAX_SEEN_IDS);
        localStorage.setItem(SEEN_IDS_KEY, JSON.stringify(ids));
        if (seenIds.current.size > MAX_SEEN_IDS) seenIds.current = new Set(ids);
      } catch (e) {}
    } catch (err) {
      if (articles.length === 0) setError("Error loading articles.");
      setInitialLoading(false);
    } finally {
      setLoading(false);
    }
  }, [loading, likedIds, articles.length, interestProfile, userInterests]);

  // EXPLORE MODE LOADER
  const loadExplore = useCallback(async (reset = false) => {
    if (!reset && exploreLoading) return;
    setExploreLoading(true);
    if (reset) setError(null);

    try {
      const excludeIds = new Set<number>(exploreSeenIds.current);
      likedIds.forEach(id => excludeIds.add(id));

      const result = await fetchArticleBatch(
        reset ? INITIAL_BATCH : SUBSEQUENT_BATCH,
        excludeIds,
        'explore',
        {
          profile: interestProfile,
          userInterests: userInterests || []
        }
      );

      const newOnes = result.articles || [];

      if (reset) {
        setExploreArticles(newOnes);
        if (newOnes.length === 0) setError("No articles found.");
      } else {
        setExploreArticles(prev => [...prev, ...newOnes]);
      }

      newOnes.forEach(a => exploreSeenIds.current.add(a.pageid));
    } catch (err) {
      if (exploreArticles.length === 0) setError("Error loading articles.");
    } finally {
      setExploreLoading(false);
    }
  }, [exploreLoading, likedIds, exploreArticles.length, interestProfile, userInterests]);

  // SEPARATE SEARCH FUNCTION
  const performSearch = useCallback(async (query: string, reset = false) => {
    if (!query.trim()) return;

    setLoading(true);
    if (reset) {
      searchSeenIds.current.clear();
      searchOffset.current = 0;
    }

    try {
      const excludeIds = new Set<number>(searchSeenIds.current);
      likedIds.forEach(id => excludeIds.add(id));

      const result = await fetchArticleBatch(
        reset ? INITIAL_BATCH : SUBSEQUENT_BATCH,
        excludeIds,
        'search',
        {
          query: query,
          offset: reset ? 0 : searchOffset.current
        }
      );

      const newOnes = result.articles || [];

      if (reset) {
        setSearchResults(newOnes);
        if (newOnes.length === 0) setError("No results found.");
      } else {
        setSearchResults(prev => [...prev, ...newOnes]);
      }

      if (result.nextOffset !== undefined) searchOffset.current = result.nextOffset;
      newOnes.forEach(a => searchSeenIds.current.add(a.pageid));
    } catch (err) {
      setError("Search failed.");
    } finally {
      setLoading(false);
    }
  }, [likedIds]);

  // Preload images for the next few articles so they're ready before you scroll to them
  const preloadUpcomingImages = useCallback((currentIdx: number) => {
    const list = viewMode === 'search' ? searchResults : (viewMode === 'liked' ? likedArticles : (viewMode === 'explore' ? exploreArticles : articles));
    for (let i = currentIdx; i <= currentIdx + 4 && i < list.length; i++) {
      const url = list[i]?.thumbnail?.source;
      if (url && !preloadedUrls.current.has(url)) {
        preloadedUrls.current.add(url);
        const img = new Image();
        img.src = url;
      }
    }
  }, [articles, exploreArticles, searchResults, likedArticles, viewMode]);

  // Preload first batch of images when articles change
  useEffect(() => {
    preloadUpcomingImages(0);
  }, [articles.length > 0, exploreArticles.length > 0, searchResults.length > 0]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight } = e.currentTarget;
    const itemsScrolled = Math.round(scrollTop / clientHeight);

    scrollPositions.current[viewMode] = scrollTop;

    // Preload images for upcoming articles
    preloadUpcomingImages(itemsScrolled);

    // Debounced snap detection for view tracking (feed and explore mode)
    if (viewMode === 'feed' || viewMode === 'explore') {
      if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
      scrollDebounceRef.current = setTimeout(() => {
        if (itemsScrolled !== currentVisibleIndex.current) {
          recordCurrentView();
          currentVisibleIndex.current = itemsScrolled;
          viewStartTime.current = Date.now();
          accumulatedModalTime.current = 0;
        }
      }, 200);
    }

    const currentList = viewMode === 'search' ? searchResults : (viewMode === 'liked' ? likedArticles : (viewMode === 'explore' ? exploreArticles : articles));
    const itemsLeft = currentList.length - itemsScrolled;

    if (itemsLeft < PREFETCH_THRESHOLD) {
      if (viewMode === 'search' && searchQuery && !loading) {
        performSearch(searchQuery, false);
      } else if (viewMode === 'feed' && !loading) {
        loadMore();
      } else if (viewMode === 'explore' && !exploreLoading) {
        loadExplore();
      }
    }
  }, [articles.length, exploreArticles.length, searchResults.length, likedArticles.length, loading, exploreLoading, viewMode, searchQuery, loadMore, loadExplore, performSearch, recordCurrentView, preloadUpcomingImages]);

  const toggleLike = (article: WikipediaArticle) => {
    setLikedArticles(prev => {
      const isAlreadyLiked = prev.some(a => a.pageid === article.pageid);
      if (isAlreadyLiked) {
        return prev.filter(a => a.pageid !== article.pageid);
      } else {
        setInterestProfile(current => updateInterestProfile(current, article));
        setReadingStats(s => ({ ...s, articlesLiked: s.articlesLiked + 1 }));
        return [article, ...prev];
      }
    });
  };

  const dismissArticle = useCallback((article: WikipediaArticle) => {
    // Remove from current list
    if (viewMode === 'feed') {
      setArticles(prev => prev.filter(a => a.pageid !== article.pageid));
    } else if (viewMode === 'explore') {
      setExploreArticles(prev => prev.filter(a => a.pageid !== article.pageid));
    } else if (viewMode === 'search') {
      setSearchResults(prev => prev.filter(a => a.pageid !== article.pageid));
    }

    // Add to seen so it won't appear again
    seenIds.current.add(article.pageid);

    // Save to persistent dismissed list
    try {
      const saved = localStorage.getItem(DISMISSED_IDS_KEY);
      const dismissed: number[] = saved ? JSON.parse(saved) : [];
      dismissed.push(article.pageid);
      localStorage.setItem(DISMISSED_IDS_KEY, JSON.stringify(dismissed.slice(-200)));
    } catch (e) {}

    // Downvote interest categories
    setInterestProfile(current => downvoteInterestProfile(current, article));
  }, [viewMode]);

  const handleSearchSubmit = (query: string) => {
    if (!query.trim()) return;

    if (viewMode === 'feed' || viewMode === 'explore') {
      recordCurrentView();
      flushSignals();
    }

    setShowSearchOverlay(false);
    setSearchQuery(query);
    setViewMode('search');
    setSearchResults([]);
    setError(null);
    setLoading(true);

    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
      scrollPositions.current.search = 0;
    }

    performSearch(query, true);
  };

  const goToFeed = () => {
    if (viewMode === 'feed' || viewMode === 'explore') {
      recordCurrentView();
      flushSignals();
    }

    setViewMode('feed');
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
    if (mainRef.current) {
      setTimeout(() => {
        if (mainRef.current) {
          mainRef.current.scrollTop = scrollPositions.current.feed || 0;
          viewStartTime.current = Date.now();
          accumulatedModalTime.current = 0;
        }
      }, 0);
    }
  };

  const goToExplore = () => {
    if (viewMode === 'feed' || viewMode === 'explore') {
      recordCurrentView();
      flushSignals();
    }

    setViewMode('explore');
    setSearchQuery('');
    setSearchResults([]);
    setError(null);

    // Load explore articles if we don't have any
    if (exploreArticles.length === 0) {
      loadExplore(true);
    }

    if (mainRef.current) {
      setTimeout(() => {
        if (mainRef.current) {
          mainRef.current.scrollTop = scrollPositions.current.explore || 0;
          viewStartTime.current = Date.now();
          accumulatedModalTime.current = 0;
        }
      }, 0);
    }
  };

  const goToLiked = () => {
    if (viewMode === 'feed' || viewMode === 'explore') {
      recordCurrentView();
      flushSignals();
    }

    setViewMode('liked');
    setSearchQuery('');
    setError(null);
    if (mainRef.current) {
      setTimeout(() => {
        if (mainRef.current) {
          mainRef.current.scrollTop = scrollPositions.current.liked || 0;
        }
      }, 0);
    }
  };

  const handleOnboardingComplete = (interests: string[]) => {
    setUserInterests(interests);
    localStorage.setItem('scrolling_interests', JSON.stringify(interests));
    setShowOnboarding(false);
    loadMore(true, interests);
  };

  const handleProfileSave = (interests: string[]) => {
    setUserInterests(interests);
    localStorage.setItem('scrolling_interests', JSON.stringify(interests));
    setArticles([]);
    setExploreArticles([]);
    exploreSeenIds.current.clear();
    setViewMode('feed');
    setSearchQuery('');
    setError(null);
    setShowProfileSettings(false);
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
      scrollPositions.current.feed = 0;
    }
    loadMore(true, interests);
  };

  useEffect(() => {
    if (!isInitialLoadRequested.current) {
      isInitialLoadRequested.current = true;

      // Check for shared article link: ?a=Article_Title
      const params = new URLSearchParams(window.location.search);
      const sharedTitle = params.get('a');

      if (sharedTitle) {
        // Clean the URL without reloading
        window.history.replaceState({}, '', window.location.pathname);

        // Fetch the shared article, show it first, then load feed behind it
        fetchArticleByTitle(sharedTitle).then(article => {
          if (article) {
            setArticles(prev => [article, ...prev]);
            seenIds.current.add(article.pageid);
          }
          setInitialLoading(false);
          loadMore(false);
        });
      } else {
        loadMore(true);
      }
    }
  }, [loadMore]);

  const displayList = viewMode === 'search' ? searchResults : (viewMode === 'liked' ? likedArticles : (viewMode === 'explore' ? exploreArticles : articles));
  const isSearchMode = viewMode === 'search';
  const isCurrentLoading = viewMode === 'explore' ? exploreLoading : loading;

  return (
    <div className="h-full w-full flex flex-col bg-black text-white relative overflow-hidden font-sans select-none">
      {/* HEADER - Hide when full article is open */}
      {!isFullArticleOpen && (
        <header className="fixed top-0 left-0 right-0 z-[60] pt-safe pointer-events-none">
        <div className="flex items-center justify-between px-6 py-6 pointer-events-auto max-w-lg mx-auto w-full">
          {isSearchMode ? (
            // SEARCH MODE HEADER
            <>
              <button
                onClick={goToFeed}
                className="w-11 h-11 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-full text-white/50 border border-white/5"
              >
                <ArrowLeft size={20} />
              </button>

              <div className="flex-1 mx-4 bg-black/40 backdrop-blur-xl rounded-full border border-white/5 px-4 py-2">
                <p className="text-sm font-bold text-white/70 truncate">"{searchQuery}"</p>
              </div>

              <button
                onClick={() => setShowSearchOverlay(true)}
                className="w-11 h-11 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-full text-white/50 border border-white/5"
              >
                <Search size={20} />
              </button>
            </>
          ) : (
            // FEED/EXPLORE/LIKED MODE HEADER
            <>
              <button
                onClick={() => setShowSearchOverlay(true)}
                className="w-11 h-11 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-full text-white/50 border border-white/5"
              >
                <Search size={20} />
              </button>

              <div className="flex items-center p-1 bg-black/40 backdrop-blur-xl rounded-full border border-white/5 shadow-2xl">
                <button
                  onClick={goToFeed}
                  className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-200 ${viewMode === 'feed' ? 'bg-white text-black shadow-lg' : 'text-white/30'}`}
                >
                  For You
                </button>
                <button
                  onClick={goToExplore}
                  className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-200 ${viewMode === 'explore' ? 'bg-white text-black shadow-lg' : 'text-white/30'}`}
                >
                  Explore
                </button>
                <button
                  onClick={goToLiked}
                  className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.15em] transition-all duration-200 ${viewMode === 'liked' ? 'bg-white text-black shadow-lg' : 'text-white/30'}`}
                >
                  Saved
                </button>
              </div>

              <button
                onClick={() => setShowProfileSettings(true)}
                className="w-11 h-11 flex items-center justify-center bg-black/40 backdrop-blur-xl rounded-full text-white/50 border border-white/5"
              >
                <User size={20} />
              </button>
            </>
          )}
        </div>
      </header>
      )}

      {/* MAIN CONTENT */}
      <main
        ref={mainRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto snap-y snap-mandatory snap-container bg-black no-scrollbar h-full"
      >
        {initialLoading && articles.length === 0 && viewMode === 'feed' ? (
          <SkeletonCard />
        ) : exploreLoading && exploreArticles.length === 0 && viewMode === 'explore' ? (
          <SkeletonCard />
        ) : error && displayList.length === 0 ? (
          <div className="h-full w-full flex flex-col items-center justify-center bg-black px-8">
            <div className="text-6xl mb-4">🔍</div>
            <p className="text-white/50 text-center">{error}</p>
            {isSearchMode && (
              <button
                onClick={goToFeed}
                className="mt-6 px-6 py-3 bg-white text-black rounded-full font-bold text-sm"
              >
                Back to Feed
              </button>
            )}
          </div>
        ) : (
          <div className="contents">
            {displayList.map((article, idx) => (
              <ArticleCard
                key={`${article.pageid}-${idx}`}
                article={article}
                isLiked={likedIds.has(article.pageid)}
                onToggleLike={() => toggleLike(article)}
                onDismiss={viewMode !== 'liked' ? () => dismissArticle(article) : undefined}
                onFullArticleChange={setIsFullArticleOpen}
                onModalTime={handleModalTime}
              />
            ))}
            {isCurrentLoading && displayList.length > 0 && (
              <div className="h-screen w-full flex items-center justify-center snap-start bg-black">
                <div className="w-6 h-6 border-2 border-white/10 border-t-white rounded-full animate-spin" />
              </div>
            )}
          </div>
        )}
      </main>

      {/* SEARCH OVERLAY */}
      {showSearchOverlay && (
        <div
          className="fixed inset-0 z-[100] flex flex-col"
          style={{ background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)' }}
        >
          {/* Tap background to close */}
          <div className="absolute inset-0" onClick={() => setShowSearchOverlay(false)} />

          <div className="relative z-10 flex flex-col items-center w-full pt-16 px-6">
            {/* Cancel row */}
            <div className="w-full max-w-lg flex justify-end mb-4">
              <button
                onClick={() => setShowSearchOverlay(false)}
                className="text-[15px] font-medium text-blue-400 active:text-blue-300 transition-colors"
              >
                Cancel
              </button>
            </div>

            {/* Search pill */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSearchSubmit(searchQuery);
              }}
              className="w-full max-w-lg"
            >
              <div className="flex items-center gap-3 bg-white/[0.08] rounded-2xl px-5 py-4 border border-white/[0.06] focus-within:border-white/15 transition-colors">
                <Search size={18} className="text-white/30 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search"
                  className="flex-1 bg-transparent outline-none text-[17px] font-normal text-white placeholder:text-white/25 caret-blue-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 active:bg-white/30 transition-colors"
                  >
                    <X size={12} className="text-black" strokeWidth={3} />
                  </button>
                )}
              </div>

              {/* Submit hint */}
              {searchQuery.trim() && (
                <button
                  type="submit"
                  className="w-full mt-4 flex items-center gap-3 px-5 py-4 rounded-2xl bg-white/[0.05] active:bg-white/[0.08] transition-colors"
                >
                  <Search size={16} className="text-blue-400 flex-shrink-0" />
                  <span className="text-[15px] text-white/80 font-normal">Search for "<span className="text-white font-medium">{searchQuery}</span>"</span>
                </button>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ONBOARDING */}
      {showOnboarding && (
        <Onboarding onComplete={handleOnboardingComplete} />
      )}

      {/* PROFILE SETTINGS */}
      {showProfileSettings && (
        <ProfileSettings
          currentInterests={userInterests || []}
          onClose={() => setShowProfileSettings(false)}
          onSave={handleProfileSave}
          stats={readingStats}
        />
      )}
    </div>
  );
};

export default App;
